import { Check, Clock } from 'lucide-react';
import type { ReactElement } from 'react';

import { formatDateTime } from '@/lib/time';

import type { FlowCapabilities, StageSlot, StageView } from '../types';

// Responsables de la etapa abierta (G2-8): quién dio su conformidad o firmó, quién falta
// (persona o puesto, y quién actuó por el puesto) y, si quien opera es Calidad sobre una
// etapa con responsables asignados, el aviso de que su acción queda como intervención.
// Con la aprobación sellada muestra el registro de lo firmado: nada falta ni se opera.

interface Props {
  stage: StageView;
  actingAs: FlowCapabilities['actingAs'];
}

const STAGE_NAME: Record<StageView['role'], string> = {
  elaborador: 'Elaboración',
  revisor: 'Revisión',
  aprobador: 'Aprobación',
  capacitador: 'Capacitación',
};

/** Verbo del acto de la etapa, o null si la etapa no tiene actos por ronda. */
function actVerb(role: StageView['role']): { done: string; count: string } | null {
  if (role === 'aprobador') {
    return { done: 'Firmó', count: 'firmas' };
  }
  if (role === 'revisor') {
    return { done: 'Dio conformidad', count: 'conformidades' };
  }
  return null;
}

function slotLine(slot: StageSlot, done: string | null): string {
  if (done === null) {
    return slot.label;
  }
  if (slot.status === 'pending') {
    return `Falta ${slot.label}`;
  }
  const when = formatDateTime(slot.actedAt);
  return slot.kind === 'position'
    ? `${slot.label}: ${done} ${slot.actedBy ?? ''} por el puesto el ${when}`
    : `${done} ${slot.actedBy ?? slot.label} el ${when}`;
}

function summaryOf(stage: StageView, done: number): string {
  const verb = actVerb(stage.role);
  if (verb === null) {
    return STAGE_NAME[stage.role];
  }
  if (stage.sealed) {
    // Firmas, no cupos: una firma puede cubrir a la persona y a su puesto.
    const signatures = stage.actCount;
    return `${STAGE_NAME[stage.role]} sellada: ${signatures} ${signatures === 1 ? 'firma' : 'firmas'}`;
  }
  return `${STAGE_NAME[stage.role]}: ${done} de ${stage.slots.length} ${verb.count}`;
}

export function StageResponsiblesCard({ stage, actingAs }: Props): ReactElement {
  const verb = actVerb(stage.role);
  const done = stage.slots.filter((s) => s.status === 'done').length;
  const intervention = !stage.sealed && actingAs === 'quality' && stage.slots.length > 0;

  return (
    <div className="card rise d2">
      <div className="ch">
        <h3>Responsables de la etapa</h3>
        <span className="count">{summaryOf(stage, done)}</span>
      </div>
      <div className="cb stack-tight">
        {intervention ? (
          <p role="note" className="act-note">
            Intervienes como Calidad: la acción queda registrada como intervención.
          </p>
        ) : null}
        {stage.slots.length === 0 ? (
          stage.sealed ? null : (
            <p className="act-note">Sin responsables asignados: la opera Calidad.</p>
          )
        ) : (
          <ul className="act-list">
            {stage.slots.map((slot) => {
              const pending = verb !== null && slot.status === 'pending';
              const Icon = verb === null || pending ? Clock : Check;
              return (
                <li key={slot.key} className="act-row">
                  <Icon aria-hidden="true" size={14} />
                  <span className={pending ? 'soft' : undefined}>
                    {slotLine(slot, verb === null ? null : verb.done)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {stage.qualityActs.map((a) => (
          <p key={`${a.actedBy}-${a.actedAt}`} className="act-note">
            {verb?.done ?? 'Actuó'} {a.actedBy} (Calidad) el {formatDateTime(a.actedAt)}
          </p>
        ))}
      </div>
    </div>
  );
}
